import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Subject, take, takeUntil } from 'rxjs';
import { LookupService } from 'src/app/services/lookup.service';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { LEPartnerService } from '../../../../services/lepartner.service';

@Component({
  selector: 'ness-lepartner-form',
  templateUrl: './lepartner-form.component.html',
  styles: ['.card-title > h3 { font-size: 1.1em !important; font-weight: 700; }',
    'form .form-row { margin-left: -15px; margin-right: -15px; }']
})
export class LEPartnerFormComponent implements OnInit, OnDestroy {
  @Input() isEventForm: boolean = false;
  @Input() eventAgencyId: string = null;
  @Input() titleText: string;
  @Output() closeForm = new EventEmitter<ServerModels.ENESS_LEPartnerDTO>();
  @Output() onAddOrUpdatePartner = new EventEmitter<ServerModels.ENESS_LEPartnerDTO>();
  agencyIdToSearch: string;

  phoneNumberFormGroup: FormGroup = this._fb.group({
    PhoneNumber: ['', Validators.required],
    PhoneNumberTypeID: ['', Validators.required],
    PhoneNumberOtherType: [{ value: null, disabled: true }, [NESSValidators.conditionalRequired(() => this.phoneNumberFormGroup.get('PhoneNumberTypeID').value.toLowerCase() == this.lookupPhoneNumberType('Other')), Validators.maxLength(80)]],
    IsPrimary: false
  });

  emailFormGroup: FormGroup = this._fb.group({
    LEPartnersEmailAccountStagingID: null,
    LEPartnerStagingID: '',
    LEPartnersEmailAccountsID: '',
    LEPartnerID: '',
    EmailAccount: ['', Validators.maxLength(128)],
    EmailAccountStatus: ['', Validators.maxLength(30)],
    CreatedOn: new FormControl<Date>(null),
    CreatedBy: ''
  });

  lePartnerInputForm: FormGroup = this._fb.group({
    LEPartnerStagingID: '',
    LEPartnerID: '',
    ApplicationUserID: '',
    FirstName: ['', Validators.required],
    MiddleName: '',
    LastName: ['', Validators.required],
    Suffix: '',
    Title: '',
    Badge: '',
    AgencyID: '',
    ENESS_XREF_LEPartnersPhoneNumbers: this._fb.array([], NESSValidators.formArrayRequireOne({ value: 'IsPrimary' })),
    ENESS_XREF_LEPartnersEmailAccounts: this._fb.array([])
  });

  private _destroyed$: Subject<void> = new Subject<void>();

  private _lePartner: ServerModels.ENESS_LEPartnerDTO;
  @Input() set lePartner(newPartner: ServerModels.ENESS_LEPartnerDTO) {
    this._lePartner = newPartner;
    if (!!newPartner && typeof (this.lePartnerInputForm) !== 'undefined') {
      this.populatePartnerFormData();
    }
  }

  public get ENESS_XREF_LEPartnersEmailAccounts(): FormArray {
    return this.lePartnerInputForm.get('ENESS_XREF_LEPartnersEmailAccounts') as FormArray;
  }

  public get ENESS_XREF_LEPartnersPhoneNumbers(): FormArray {
    return this.lePartnerInputForm.get('ENESS_XREF_LEPartnersPhoneNumbers') as FormArray;
  }

  constructor(private lePartnerService: LEPartnerService,
    public lookupService: LookupService,
    private _fb: FormBuilder,
    private messageService: MessageService,
    public elementRef: ElementRef) { }

  ngOnInit() {
    window.setTimeout(() => {
      (<HTMLElement>this.elementRef.nativeElement).setAttribute("tabindex", "-1");
      (<HTMLElement>this.elementRef.nativeElement).focus();
    }, 1000);

    this.phoneNumberFormGroup.get('PhoneNumberTypeID').valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((value:string) => {
      if (!!value && value.toLowerCase() == this.lookupPhoneNumberType('Other')) {
        this.phoneNumberFormGroup.get('PhoneNumberOtherType').enable();
      } else {
        this.phoneNumberFormGroup.get('PhoneNumberOtherType').setValue(null);
        this.phoneNumberFormGroup.get('PhoneNumberOtherType').disable();
      }
    });
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  public onSelectedUser(data: ServerModels.ENESS_LEPartnerDTO): void {
    if (!data || !Object.keys(data).length || !this.lePartnerInputForm) {
      return;
    }
    this.lePartner = data;
    this.ENESS_XREF_LEPartnersEmailAccounts.clear();
    this.createEmailFormGroups();
    this.ENESS_XREF_LEPartnersPhoneNumbers.clear();
    this.createPhoneNumberFormGroups();
    this.patchLEPartnerInputForm();
  }

  public onSaveLePartner(): void {
    if (this.lePartnerInputForm.invalid) {
      return;
    }

    if (this.ENESS_XREF_LEPartnersEmailAccounts?.length > 0) {
      if (!this.ENESS_XREF_LEPartnersEmailAccounts.at(0).get('EmailAccount').value) {
        this.ENESS_XREF_LEPartnersEmailAccounts.clear();
      } 
    }

    const formModel = this.lePartnerInputForm.value;
    this._lePartner = { ...this.lePartner, ...formModel };

    if (!this.isEventForm) {
      this.onAddOrUpdatePartner.emit(this._lePartner);
      this.clearAllFields();
    } else {
      this.addOrUpdateLEPartner();
    }
  }

  public insertPhoneNumber(phoneType?: string): void {
    if (!this.phoneNumberFormGroup.valid)
      return;

    if (this.phoneNumberFormGroup.get('IsPrimary').value == true) {
      (<FormArray>this.lePartnerInputForm.get('ENESS_XREF_LEPartnersPhoneNumbers')).controls.forEach((ctrl: FormGroup) => ctrl.get('IsPrimary').setValue(false));
    }

    let phoneNumber = this._fb.group({
      LEPartnersPhoneNumberStagingID: null,
      LEPartnersPhoneNumbersID: null,
      LEPartnerStagingID: this.lePartnerInputForm.get('LEPartnerStagingID').value,
      LEPartnerID: this.lePartnerInputForm.get('LEPartnerID').value,
      PhoneNumber: [this.phoneNumberFormGroup.get('PhoneNumber').value, Validators.required],
      PhoneNumberTypeID: [this.phoneNumberFormGroup.get('PhoneNumberTypeID').value, Validators.required],
      PhoneNumberType: phoneType,
      PhoneNumberOtherType: [{ value: this.phoneNumberFormGroup.get('PhoneNumberOtherType').value, disabled: this.phoneNumberFormGroup.get('PhoneNumberOtherType').disabled }, NESSValidators.conditionalRequired(() => this.phoneNumberFormGroup.get('PhoneNumberTypeID').value?.toLowerCase() == this.lookupPhoneNumberType('Other'))],
      IsPrimary: this.phoneNumberFormGroup.get('IsPrimary').value || false,
      StartDate: null,
      EndDate: null,
      CreatedOn: null,
      Deleted: false
    });

    if (phoneNumber.get('IsPrimary').value == true) {
      (<FormArray>this.lePartnerInputForm.get('ENESS_XREF_LEPartnersPhoneNumbers')).insert(0, phoneNumber);
    } else {
      (<FormArray>this.lePartnerInputForm.get('ENESS_XREF_LEPartnersPhoneNumbers')).push(phoneNumber);
    }

    this._lePartner.ENESS_XREF_LEPartnersPhoneNumbers.push(this.lePartnerInputForm.value);
    this.phoneNumberFormGroup.reset({
      PhoneNumberTypeID: '',
      IsPrimary: false
    });
  }

  public removePhoneNumber(idx: number): void {
    this.ENESS_XREF_LEPartnersPhoneNumbers.removeAt(idx);
    this.ENESS_XREF_LEPartnersPhoneNumbers.updateValueAndValidity();
  }

  public onClose(): void {
    this.closeForm.emit(this._lePartner);
    this.clearAllFields();
  }

  public onSelectPrimaryPhoneNumber(rowIdx: number): void {
    this.ENESS_XREF_LEPartnersPhoneNumbers.controls.forEach((x:AbstractControl, i:number) => {
      x.get('IsPrimary').setValue(i == rowIdx);
    });
  }

  public removeItem(idx: number, property: string, control: string): void {
    this[property].splice(idx, 1);
    this.lePartnerInputForm.get(control).setValue([...this[property]]);
  }

  public lookupPhoneNumberType(typeName: string): string | null {
    const typeObj: ServerModels.ENESS_REF_PhoneNumberTypes = this.lookupService.phoneNumberTypesSubj.getValue().find((x: ServerModels.ENESS_REF_PhoneNumberTypes) => x.PhoneNumberType == typeName);

    if (typeObj && Object.keys(typeObj).length) {
      return typeObj.PhoneNumberTypeID.toLowerCase();
    }

    return null;
  }

  private addOrUpdateLEPartner(): void {
    this.lePartnerService.addOrUpdateLEPartnerStagingAsync(this._lePartner).pipe(take(1)).subscribe(
      (partner: ServerModels.ENESS_LEPartnerDTO) => {
        if (partner) {
          this.onAddOrUpdatePartner.emit(partner);
          this.clearAllFields();
        }
      },
      (err:any) => this.messageService.add({ key: 'lePartnerFormMsg', severity: 'error', summary: 'Error:', detail: err.error || err.error.ExceptionMessage || err.error.Message })
    );
  }

  private clearAllFields(): void {
    this.emailFormGroup.reset();
    this.phoneNumberFormGroup.reset();
    this.lePartnerInputForm.reset({
      LEPartnerStagingID: null,
      LEPartnerID: null,
      ApplicationUserID: null,
      AgencyID: null,
      FirstName:'',
      MiddleName: '',
      LastName: '',
      Suffix: '',
      Title: '',
      ENESS_XREF_LEPartnersPhoneNumbers: [],
      ENESS_XREF_LEPartnersEmailAccounts: [this.emailFormGroup],
      Badge: ''
    });
    this.lePartner = null;
  }

  private populatePartnerFormData() {
    this.createEmailFormGroups();
    this.createPhoneNumberFormGroups();
    this.patchLEPartnerInputForm();
  }

  private createEmailFormGroups(): void {
    if (this._lePartner.ENESS_XREF_LEPartnersEmailAccounts.length > 0) {
      this._lePartner.ENESS_XREF_LEPartnersEmailAccounts.forEach(x => {
        this.ENESS_XREF_LEPartnersEmailAccounts.push(this._fb.group({
          LEPartnersEmailAccountStagingID: x.LEPartnersEmailAccountStagingID,
          LEPartnerStagingID: x.LEPartnerStagingID,
          LEPartnersEmailAccountsID: x.LEPartnersEmailAccountsID,
          LEPartnerID: x.LEPartnerID,
          EmailAccount: [x.EmailAccount, Validators.maxLength(128)],
          EmailAccountStatus: [x.EmailAccountStatus || '', Validators.maxLength(30)],
          CreatedOn: x.CreatedOn,
          CreatedBy: x.CreatedBy
        }));
      });
    } else {
      this.emailFormGroup.patchValue({
            LEPartnerStagingID: this._lePartner.LEPartnerStagingID,
            LEPartnerID: this._lePartner.LEPartnerID
      });
      this.ENESS_XREF_LEPartnersEmailAccounts.push(this.emailFormGroup);
    }
  }

  private createPhoneNumberFormGroups(): void {
    this._lePartner.ENESS_XREF_LEPartnersPhoneNumbers.forEach(p => {
      (<FormArray>this.lePartnerInputForm.get('ENESS_XREF_LEPartnersPhoneNumbers')).push(this._fb.group({
        LEPartnersPhoneNumberStagingID: p.LEPartnersPhoneNumberStagingID,
        LEPartnersPhoneNumbersID: p.LEPartnersPhoneNumbersID,
        LEPartnerStagingID: p.LEPartnerStagingID,
        LEPartnerID: p.LEPartnerID,
        PhoneNumber: [p.PhoneNumber, Validators.required],
        PhoneNumberTypeID: [p.PhoneNumberTypeID, Validators.required],
        PhoneNumberType: p.PhoneNumberType,
        PhoneNumberOtherType: p.PhoneNumberOtherType,
        IsPrimary: p.IsPrimary || false,
        StartDate: p.StartDate,
        EndDate: p.EndDate,
        CreatedOn: p.CreatedOn,
        Deleted: false
      }));
    });
  }

  private patchLEPartnerInputForm(): void {
    let appUserId = null;
    if (this._lePartner.ApplicationUserID) {
      appUserId = this._lePartner.ApplicationUserID.trim();
    }

    this.lePartnerInputForm.patchValue({
      LEPartnerStagingID: (this._lePartner.LEPartnerStagingID ? this._lePartner.LEPartnerStagingID.trim() : null),
      LEPartnerID: (this._lePartner.LEPartnerID ? this._lePartner.LEPartnerID.trim() : null),
      ApplicationUserID: appUserId,
      AgencyID: this.eventAgencyId ?? this._lePartner.AgencyID,
      FirstName: (this._lePartner.FirstName ? this._lePartner.FirstName.trim() : ''),
      MiddleName: (this._lePartner.MiddleName ? this._lePartner.MiddleName.trim() : ''),
      LastName: (this._lePartner.LastName ? this._lePartner.LastName.trim() : ''),
      Suffix: (this._lePartner.Suffix ? this._lePartner.Suffix.trim() : ''),
      Title: (this._lePartner.Title ? this._lePartner.Title.trim() : ''),
      Badge: (this._lePartner.Badge ? this._lePartner.Badge.trim() : '')
    });
  }
}
